/**
 * LiveKitViewer — lightweight WebRTC live stream viewer for browser sessions.
 *
 * Uses livekit-client SDK (dynamically imported for code-splitting) to connect
 * to a LiveKit room and subscribe to the video track published by the
 * browser pod's publisher sidecar.
 *
 * Props:
 *   token      — JWT viewer token from CloudTest Infra
 *   livekitUrl — LiveKit server WSS URL (e.g. wss://livekit.qa-platform.local)
 *   roomName   — LiveKit room name (for display only)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';

interface LiveKitViewerProps {
  token: string;
  livekitUrl: string;
  roomName?: string;
  /** Called when LiveKit fails to deliver video (error or timeout). Parent can fall back to VNC. */
  onFallback?: () => void;
  /** Seconds to wait for a video track after connecting before triggering onFallback. Default: 10 */
  fallbackTimeoutSeconds?: number;
}

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

const LiveKitViewer: React.FC<LiveKitViewerProps> = ({ token, livekitUrl, roomName, onFallback, fallbackTimeoutSeconds = 10 }) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [hasVideo, setHasVideo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomRef = useRef<any>(null);
  const fallbackFired = useRef(false);

  const attachTrack = useCallback((track: any) => {
    if (videoRef.current) {
      track.attach(videoRef.current);
      setHasVideo(true);
    }
  }, []);

  const connect = useCallback(async () => {
    setConnectionState('connecting');
    setError(null);
    setHasVideo(false);

    try {
      const { Room, RoomEvent, Track } = await import('livekit-client');

      const room = new Room();
      roomRef.current = room;

      room.on(RoomEvent.Connected, () => {
        setConnectionState('connected');
      });

      room.on(RoomEvent.Disconnected, () => {
        setConnectionState('disconnected');
        setHasVideo(false);
      });

      // Handle new track subscriptions (publisher starts after we join)
      room.on(RoomEvent.TrackSubscribed, (track: any) => {
        if (track.kind === Track.Kind.Video) {
          attachTrack(track);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: any) => {
        if (track.kind === Track.Kind.Video) {
          track.detach();
          setHasVideo(false);
        }
      });

      await room.connect(livekitUrl, token);

      // After connecting, attach any already-published video tracks
      // (publisher sidecar may have started before we joined)
      room.remoteParticipants.forEach((participant: any) => {
        participant.trackPublications.forEach((pub: any) => {
          if (pub.track && pub.track.kind === Track.Kind.Video) {
            attachTrack(pub.track);
          }
        });
      });
    } catch (err: any) {
      console.error('LiveKit connection failed:', err);
      setError(err.message || 'Failed to connect to live stream');
      setConnectionState('error');
    }
  }, [token, livekitUrl, attachTrack]);

  const disconnect = useCallback(() => {
    try {
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    } catch {
      // Ignore cleanup errors
    }
    setConnectionState('disconnected');
    setHasVideo(false);
  }, []);

  // Auto-connect on mount, disconnect on unmount
  useEffect(() => {
    if (token && livekitUrl) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [token, livekitUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback timeout: if connected but no video after N seconds, notify parent
  useEffect(() => {
    if (hasVideo || !onFallback || fallbackFired.current) return;
    if (connectionState === 'error') {
      fallbackFired.current = true;
      onFallback();
      return;
    }
    if (connectionState !== 'connecting' && connectionState !== 'connected') return;
    const timer = setTimeout(() => {
      if (!fallbackFired.current && !hasVideo) {
        fallbackFired.current = true;
        console.warn(`LiveKit: no video after ${fallbackTimeoutSeconds}s, triggering VNC fallback`);
        onFallback();
      }
    }, fallbackTimeoutSeconds * 1000);
    return () => clearTimeout(timer);
  }, [connectionState, hasVideo, onFallback, fallbackTimeoutSeconds]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#000' }}>
      {/* LIVE badge & controls overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pointerEvents: 'auto' }}>
          {connectionState === 'connected' && hasVideo && (
            <Chip
              label="LIVE"
              size="small"
              sx={{
                bgcolor: '#4caf50',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '0.65rem',
                height: 20,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            />
          )}
          {roomName && connectionState === 'connected' && hasVideo && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem' }}>
              {roomName}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={0.5} sx={{ pointerEvents: 'auto' }}>
          {connectionState === 'error' && (
            <Tooltip title="Retry connection">
              <IconButton size="small" onClick={connect} sx={{ color: '#f44336', bgcolor: 'rgba(0,0,0,0.5)' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {connectionState === 'disconnected' && (
            <Tooltip title="Connect">
              <IconButton size="small" onClick={connect} sx={{ color: '#aaa', bgcolor: 'rgba(0,0,0,0.5)' }}>
                <VideocamIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Video element — only visible when a track is attached */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: hasVideo ? 'block' : 'none',
        }}
      />

      {/* Connecting state */}
      {connectionState === 'connecting' && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 1.5,
        }}>
          <CircularProgress size={36} sx={{ color: '#4caf50' }} />
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            Connecting to live stream...
          </Typography>
        </Box>
      )}

      {/* Connected but waiting for video track from publisher sidecar */}
      {connectionState === 'connected' && !hasVideo && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 1.5,
        }}>
          <CircularProgress size={36} sx={{ color: '#4caf50' }} />
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            Waiting for browser video stream...
          </Typography>
          <Typography variant="caption" sx={{ color: '#666' }}>
            Connected to room, waiting for publisher
          </Typography>
        </Box>
      )}

      {/* Disconnected state */}
      {connectionState === 'disconnected' && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 1.5,
        }}>
          <VideocamOffIcon sx={{ fontSize: 48, color: '#555' }} />
          <Typography variant="body2" sx={{ color: '#888' }}>
            Stream not connected
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {connectionState === 'error' && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3,
        }}>
          <VideocamOffIcon sx={{ fontSize: 48, color: '#f44336' }} />
          <Typography variant="body2" sx={{ color: '#f44336', textAlign: 'center' }}>
            {error || 'Failed to connect'}
          </Typography>
          <Chip
            label="Retry"
            icon={<RefreshIcon />}
            onClick={connect}
            clickable
            size="small"
            sx={{ bgcolor: '#333', color: '#fff' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default LiveKitViewer;
